import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Heading,
  Badge,
  Button,
  Icon,
  Card,
  CardBody,
  CardHeader,
  useToast,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
  Image,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaGlobe, 
  FaEnvelope, 
  FaSearch,
  FaFlask,
  FaClipboardCheck,
  FaDollarSign,
  FaDirections,
  FaExclamationTriangle,
  FaRocket,
  FaUsers,
  FaChartLine,
  FaShieldAlt,
  FaBuilding,
  FaStar,
  FaClock
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function LabProfile() {
  const { labId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [labData, setLabData] = useState(null);
  const [tests, setTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const toast = useToast();
  
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.800, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    fetchLabData();
  }, [labId]);

  useEffect(() => {
    if (searchTerm.length > 1 && tests.length > 0) {
      const filtered = tests.filter(test =>
        test.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.lab_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTests(filtered);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [searchTerm, tests]);

  const handleSignupNavigation = () => {
    // Navigate to lab-specific signup page if available, otherwise generic with lab data
    if (labData?.short_slug) {
      navigate(`/lab-signup/${labData.short_slug}`);
    } else {
      // Fallback to generic signup with lab data in URL and state
      navigate(`/lab-signup?labId=${labData?.id}`, {
        state: {
          labId: labData?.id,
          labData: labData,
          preselectedLab: true
        }
      });
    }
  };

  const fetchLabData = async () => {
    try {
      setLoading(true);
      
      // Fetch lab data
      const { data: labData, error: labError } = await supabase
        .from('labs')
        .select('*')
        .eq('id', labId)
        .single();

      if (labError) throw labError;
      if (!labData) {
        toast({
          title: 'Lab not found',
          description: 'The requested lab does not exist.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/lab-directory');
        return;
      }

      setLabData(labData);

      // Fetch tests offered by this lab
      const [testOfferingsResult, panelOfferingsResult] = await Promise.all([
        supabase
          .from('lab_test_offerings')
          .select(`
            *,
            tests (*)
          `)
          .eq('lab_id', labData.id)
          .eq('is_offered', true),
        supabase
          .from('lab_panel_offerings')
          .select(`
            *,
            panels (*)
          `)
          .eq('lab_id', labData.id)
          .eq('is_offered', true)
      ]);

      if (testOfferingsResult.error) throw testOfferingsResult.error;
      if (panelOfferingsResult.error) throw panelOfferingsResult.error;

      // Combine tests and panels
      const combinedTests = [
        ...(testOfferingsResult.data || []).map(offering => ({
          ...offering.tests,
          type: 'test',
          lab_price: offering.lab_cash_price,
          lab_code: offering.lab_test_code
        })),
        ...(panelOfferingsResult.data || []).map(offering => ({
          ...offering.panels,
          type: 'panel',
          lab_price: offering.lab_cash_price,
          lab_code: offering.lab_test_code
        }))
      ];

      setTests(combinedTests);

    } catch (error) {
      console.error('Error fetching lab data:', error);
    toast({
        title: 'Error loading lab data',
        description: 'There was a problem loading the lab information.',
        status: 'error',
      duration: 5000,
      isClosable: true,
    });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box minHeight="100vh" bg={bgGradient} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textColor}>Loading laboratory information...</Text>
        </VStack>
      </Box>
    );
  }

  if (!labData) {
    return (
      <Box minHeight="100vh" bg={bgGradient} display="flex" alignItems="center" justifyContent="center">
        <Alert status="error" maxW="md" borderRadius="md">
          <AlertIcon />
          Laboratory not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bg={bgGradient}>
      {/* Hero Section */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={8}>
        <Container maxW="container.xl">
          <HStack spacing={6} align="start">
            {labData.logo_url && (
              <Image
                src={labData.logo_url}
                alt={`${labData.name} logo`}
                boxSize="80px"
                objectFit="contain"
                borderRadius="md"
                border="1px"
                borderColor={borderColor}
                p={2}
                bg="white"
              />
            )}
            <VStack align="start" spacing={3} flex="1">
              <Heading size="xl" color={headingColor}>
                {labData.name}
                  </Heading>
              <HStack spacing={4} wrap="wrap">
                <Badge colorScheme={labData.is_active ? 'green' : 'red'} size="lg">
                  {labData.is_active ? 'Active Laboratory' : 'Inactive'}
                    </Badge>
                <HStack spacing={1} color={textColor}>
                  <Icon as={FaMapMarkerAlt} />
                  <Text fontSize="sm">{labData.address}</Text>
                </HStack>
                  </HStack>
              <HStack spacing={6} wrap="wrap">
                {labData.contact_phone && (
                  <HStack spacing={1} color={textColor}>
                    <Icon as={FaPhone} color="green.500" />
                    <Text fontSize="sm">{labData.contact_phone}</Text>
                </HStack>
                )}
                {labData.contact_email && (
                  <HStack spacing={1} color={textColor}>
                    <Icon as={FaEnvelope} color="blue.500" />
                    <Text fontSize="sm">{labData.contact_email}</Text>
                  </HStack>
                )}
                  </HStack>
                </VStack>
          </HStack>
        </Container>
      </Box>

      {/* Main Content with Sidebar */}
      <Container maxW="container.xl" py={8}>
        <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={8}>
          {/* Main Content - 3/4 width */}
          <Box gridColumn={{ base: "1", lg: "1 / 4" }}>
            <VStack spacing={8} align="stretch">
              {/* Lab Information */}
              <Card>
                <CardHeader>
                  <Heading size="md" color={headingColor}>
                    <Icon as={FaBuilding} mr={3} color="blue.500" />
                    Laboratory Information
                  </Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <Stat>
                      <StatLabel>Tests Available</StatLabel>
                      <StatNumber color="blue.600">{tests.length}</StatNumber>
                      <StatHelpText>Diagnostic tests</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Status</StatLabel>
                      <StatNumber color={labData.is_active ? "green.600" : "red.600"}>
                        {labData.is_active ? 'Active' : 'Inactive'}
                      </StatNumber>
                      <StatHelpText>Laboratory status</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Location</StatLabel>
                      <StatNumber fontSize="lg" color="purple.600">
                        <Icon as={FaMapMarkerAlt} mr={2} />
                        Available
                      </StatNumber>
                      <StatHelpText>Address provided</StatHelpText>
                    </Stat>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* Test Search & Results */}
              {tests.length > 0 && (
                <Card>
                  <CardHeader>
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color={headingColor}>
                        <Icon as={FaFlask} mr={3} color="green.500" />
                        Laboratory Tests & Pricing
                      </Heading>
                      <Box position="relative">
                        <InputGroup size="lg">
                          <InputLeftElement>
                            <Icon as={FaSearch} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            placeholder="Search tests, codes, or categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => searchTerm.length > 1 && setShowDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                            bg="white"
                            border="2px"
                            borderColor="gray.200"
                            _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #3182CE" }}
                          />
                        </InputGroup>
                        
                        {/* Search Dropdown */}
                        {showDropdown && filteredTests.length > 0 && (
                      <Box
                        position="absolute"
                            top="100%"
                            left="0"
                            right="0"
                            bg="white"
                            border="1px"
                            borderColor={borderColor}
                            borderRadius="md"
                            boxShadow="lg"
                            maxH="300px"
                            overflowY="auto"
                            zIndex={10}
                          >
                            {filteredTests.slice(0, 8).map((test, index) => (
                              <Box
                                key={index}
                                p={3}
                                borderBottom={index < 7 ? "1px" : "none"}
                                borderColor={borderColor}
                                _hover={{ bg: "gray.50" }}
                                cursor="pointer"
                              >
                                <HStack justify="space-between">
                                  <VStack align="start" spacing={1}>
                                    <HStack>
                                      <Icon as={test.type === 'panel' ? FaClipboardCheck : FaFlask} color="blue.500" />
                                      <Text fontWeight="medium">{test.name}</Text>
                                      {test.lab_code && (
                                        <Badge colorScheme="blue" size="sm">{test.lab_code}</Badge>
                                      )}
                                    </HStack>
                                    <Text fontSize="sm" color={textColor}>{test.category}</Text>
                                  </VStack>
                                  {test.lab_price && (
                                    <HStack>
                                      <Icon as={FaDollarSign} color="green.500" boxSize={3} />
                                      <Text fontWeight="bold" color="green.600">
                                        {test.lab_price.toFixed(2)}
                                      </Text>
                                    </HStack>
                                  )}
                          </HStack>
                        </Box>
                            ))}
                      </Box>
                        )}
                  </Box>
                    </VStack>
                </CardHeader>
                <CardBody pt={0}>
                    <Tabs variant="enclosed" colorScheme="blue">
                    <TabList>
                        <Tab>All Tests ({tests.length})</Tab>
                        <Tab>Tests ({tests.filter(t => t.type === 'test').length})</Tab>
                        <Tab>Panels ({tests.filter(t => t.type === 'panel').length})</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel px={0}>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Test/Panel Name</Th>
                                <Th>Type</Th>
                                <Th>Code</Th>
                                <Th isNumeric>Cash Price</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {tests.map((test, index) => (
                                <Tr key={index}>
                                  <Td>
                                    <HStack>
                                      <Icon 
                                        as={test.type === 'panel' ? FaClipboardCheck : FaFlask} 
                                        color={test.type === 'panel' ? "purple.500" : "blue.500"} 
                                      />
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="medium">{test.name}</Text>
                                        {test.category && (
                                          <Text fontSize="sm" color={textColor}>{test.category}</Text>
                                        )}
                                      </VStack>
                            </HStack>
                                  </Td>
                                  <Td>
                                    <Badge colorScheme={test.type === 'panel' ? "purple" : "blue"}>
                                      {test.type === 'panel' ? 'Panel' : 'Test'}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    {test.lab_code && (
                                      <Badge variant="outline" colorScheme="gray">
                                        {test.lab_code}
                                      </Badge>
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {test.lab_price ? (
                                      <Text fontWeight="bold" color="green.600">
                                        ${test.lab_price.toFixed(2)}
                                      </Text>
                                    ) : (
                                      <Text color={textColor}>Contact lab</Text>
                                    )}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                      </TabPanel>
                      <TabPanel px={0}>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Test Name</Th>
                                <Th>Code</Th>
                                <Th isNumeric>Cash Price</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {tests.filter(t => t.type === 'test').map((test, index) => (
                                <Tr key={index}>
                                  <Td>
                                    <HStack>
                                      <Icon as={FaFlask} color="blue.500" />
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="medium">{test.name}</Text>
                                        {test.category && (
                                          <Text fontSize="sm" color={textColor}>{test.category}</Text>
                                        )}
                                      </VStack>
                            </HStack>
                                  </Td>
                                  <Td>
                                    {test.lab_code && (
                                      <Badge variant="outline" colorScheme="gray">
                                        {test.lab_code}
                                      </Badge>
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {test.lab_price ? (
                                      <Text fontWeight="bold" color="green.600">
                                        ${test.lab_price.toFixed(2)}
                                      </Text>
                                    ) : (
                                      <Text color={textColor}>Contact lab</Text>
                                    )}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TabPanel>
                        <TabPanel px={0}>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Panel Name</Th>
                                <Th>Code</Th>
                                <Th isNumeric>Cash Price</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {tests.filter(t => t.type === 'panel').map((test, index) => (
                                <Tr key={index}>
                                  <Td>
                                    <HStack>
                                      <Icon as={FaClipboardCheck} color="purple.500" />
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="medium">{test.name}</Text>
                                        {test.category && (
                                          <Text fontSize="sm" color={textColor}>{test.category}</Text>
                                        )}
                        </VStack>
                                    </HStack>
                                  </Td>
                                  <Td>
                                    {test.lab_code && (
                                      <Badge variant="outline" colorScheme="gray">
                                        {test.lab_code}
                                      </Badge>
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {test.lab_price ? (
                                      <Text fontWeight="bold" color="green.600">
                                        ${test.lab_price.toFixed(2)}
                                      </Text>
                                    ) : (
                                      <Text color={textColor}>Contact lab</Text>
                                    )}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </CardBody>
              </Card>
              )}

              {/* No Tests Message */}
              {tests.length === 0 && (
              <Card>
                  <CardBody textAlign="center" py={8}>
                    <VStack spacing={4}>
                      <Icon as={FaFlask} boxSize={12} color="gray.400" />
                      <VStack spacing={2}>
                        <Heading size="md" color={headingColor}>No Tests Available</Heading>
                        <Text color={textColor}>
                          This laboratory hasn't listed their available tests yet.
                        </Text>
                      </VStack>
                    </VStack>
                </CardBody>
              </Card>
              )}
            </VStack>
          </Box>

          {/* Right Sidebar - 1/4 width */}
          <Box gridColumn={{ base: "1", lg: "4" }}>
            <VStack spacing={6} align="stretch">
              {/* Own This Laboratory Card */}
              <Card bg="orange.50" border="2px" borderColor="orange.300">
                <CardBody p={6}>
                  <VStack spacing={4} textAlign="center">
                    <Badge colorScheme="orange" size="lg" px={3} py={1} borderRadius="full">
                      UNCLAIMED LISTING
                    </Badge>
                    <Icon as={FaExclamationTriangle} boxSize={8} color="orange.500" />
                    <VStack spacing={2}>
                      <Heading size="md" color="orange.800">
                        Own This Laboratory?
                      </Heading>
                      <Text fontSize="sm" color="orange.700" textAlign="center">
                        Claim your listing to update information, manage your profile, and connect with more patients.
                      </Text>
                    </VStack>
                    
                    <Divider borderColor="orange.300" />
                    
                    <VStack spacing={3} w="full">
                      <HStack spacing={3} w="full" justify="center">
                        <VStack spacing={1} textAlign="center">
                          <Text fontSize="xl" fontWeight="bold" color="orange.800">75K+</Text>
                          <Text fontSize="xs" color="orange.600">Monthly Searches</Text>
                        </VStack>
                        <VStack spacing={1} textAlign="center">
                          <Text fontSize="xl" fontWeight="bold" color="orange.800">350+</Text>
                          <Text fontSize="xs" color="orange.600">Partner Labs</Text>
                        </VStack>
                      </HStack>
                      
                      <Button
                        colorScheme="orange"
                        size="md"
                        w="full"
                        leftIcon={<Icon as={FaRocket} />}
                        onClick={handleSignupNavigation}
                        fontWeight="bold"
                        _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
                      >
                        Claim & Partner
                      </Button>
                      
                      <Text fontSize="xs" color="orange.600" textAlign="center">
                        No setup fees • Start in 24 hours
                    </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Location Map Card */}
              <Card>
                <CardHeader>
                  <HStack justify="space-between">
                    <Heading size="md" color={headingColor}>
                      <Icon as={FaMapMarkerAlt} mr={2} color="red.500" />
                      Location
                    </Heading>
                    <Button
                      leftIcon={<Icon as={FaDirections} />}
                      colorScheme="blue"
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(labData.address)}`, '_blank')}
                    >
                      Directions
                    </Button>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={4} align="stretch">
                    {/* Map */}
                    <Box
                      height="200px"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={borderColor}
                      bg="gray.50"
                      position="relative"
                    >
                      <iframe
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=-118.27,-34.05,-118.23,-34.01&layer=mapnik&marker=34.0522,-118.2437`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        title={`Map showing location of ${labData.name}`}
                      />
                    </Box>
                    
                    {/* Address */}
                        <VStack align="start" spacing={2}>
                      <HStack>
                        <Icon as={FaMapMarkerAlt} color="red.500" />
                        <Text fontWeight="medium" color={headingColor}>Address</Text>
                            </HStack>
                      <Text fontSize="sm" color={textColor} pl={6}>
                        {labData.address}
                            </Text>
                        </VStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Quick Contact Card */}
              {(labData.contact_phone || labData.contact_email) && (
              <Card>
                <CardHeader>
                    <Heading size="md" color={headingColor}>
                      <Icon as={FaPhone} mr={2} color="green.500" />
                      Quick Contact
                    </Heading>
                </CardHeader>
                <CardBody pt={0}>
                    <VStack spacing={3} align="stretch">
                      {labData.contact_phone && (
                    <Button 
                          leftIcon={<Icon as={FaPhone} />}
                          colorScheme="green"
                      variant="outline" 
                          size="sm"
                          onClick={() => window.location.href = `tel:${labData.contact_phone}`}
                    >
                          {labData.contact_phone}
                    </Button>
                      )}
                      {labData.contact_email && (
                    <Button 
                          leftIcon={<Icon as={FaEnvelope} />}
                          colorScheme="blue"
                      variant="outline" 
                          size="sm"
                          onClick={() => window.location.href = `mailto:${labData.contact_email}`}
                    >
                          Email Lab
                    </Button>
                      )}
                  </VStack>
                </CardBody>
              </Card>
              )}
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
} 